import type { FC } from 'react';
import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { isTruthy } from 'app/utils/types';
import { sort } from 'app/utils/array';
import ArrowIcon from 'app/icons/arrow-min.svg';
import TeamAvatar from 'app/components/TeamAvatar';
import Tooltip from 'app/components/Tooltip';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import { AVAILABLE_PROPERTIES, getPlayerStat } from './utils';
import style from './style.module.scss';

interface Props {
  data: PlayersStatsBlockMatch;
}

const PlayersTable: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  // Sort properties in this array by wanted display order
  const [selectedProperty, setSelectedProperty] = useState(0);
  // Reverse = true allow to have highest to lowest
  const [reverse, setReverse] = useState(true);

  const changeSelection = (index: number) => {
    if (index === selectedProperty) {
      setReverse(!reverse);
    } else {
      setSelectedProperty(index);
      setReverse(true);
    }
  };

  const properties = useMemo(
    () => AVAILABLE_PROPERTIES[data.videogame_slug],
    [data.videogame_slug]
  );

  const players = useMemo(() => {
    const opponent1Players =
      Object.values(data.opponents.at(0)?.stats?.players || []).map(player => ({
        ...player,
        opponent: data.opponents.at(0)
      })) ?? [];

    const opponent2Players =
      Object.values(data.opponents.at(1)?.stats?.players || []).map(player => ({
        ...player,
        opponent: data.opponents.at(1)
      })) ?? [];

    return [...opponent1Players, ...opponent2Players].map(player =>
      getPlayerStat(player, data.videogame_slug)
    );
  }, [data]);

  const maxValues = useMemo(
    () =>
      properties
        ? properties.map((property, index) => {
            const mathFunction = property.order === 'descending' ? Math.max : Math.min;
            return mathFunction(...players.map(player => player.stats[index]).filter(isTruthy));
          })
        : [],
    [players]
  );

  const sortedPlayers = useMemo(() => {
    const list = sort(players, player => player.stats[selectedProperty]);
    if (properties?.[selectedProperty]?.order === 'ascending') {
      list.reverse();
    }
    if (reverse) {
      return list.reverse();
    }
    return list;
  }, [selectedProperty, reverse, players]);

  return (
    <BlockTemplate
      title={t('blocks_players-stats_title')}
      withPeriodSelector
      containerClassname={style.playersStatsBlockContent}
      hasData={Boolean(sortedPlayers.length)}
    >
      <table className={style.table}>
        <thead className={style.header}>
          <tr>
            <td aria-hidden />
            {/* TODO: enforce type */}
            {properties?.map((header, index) => {
              const key = `blocks_players-stats_videogame_${data.videogame_slug}_${header.name}`;
              const cellClassname = classNames({
                [style.notOnCompactVersion]: !header.compactVersion
              });
              const headerTitle = classNames({
                [style.selected]: index === selectedProperty
              });
              const upArrowClassname = classNames(style.arrow, {
                [style.selected]: index === selectedProperty && !reverse
              });
              const downArrowClassname = classNames(style.arrow, style.reverse, {
                [style.selected]: index === selectedProperty && reverse
              });

              return (
                <th key={key} className={cellClassname}>
                  <button onClick={() => changeSelection(index)}>
                    <Tooltip
                      content={t(`${key}_tooltip`)}
                      alignment={index === properties.length - 1 ? 'start' : 'middle'}
                    >
                      <span className={style.headerTitle}>
                        <span className={headerTitle}>{t(`${key}_title`)}</span>
                        <span className={style.arrowsContainer}>
                          <ArrowIcon className={upArrowClassname} />
                          <ArrowIcon className={downArrowClassname} />
                        </span>
                      </span>
                    </Tooltip>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={style.body}>
          {sortedPlayers.map(player => (
            // One line by player
            <tr key={player.id} className={style.line}>
              <td className={style.cell}>
                <div className={style.player}>
                  {player.opponent && <TeamAvatar team={player.opponent} withBackground noAlt />}
                  <span>{player.name}</span>
                </div>
              </td>
              {player.stats.map((stat, index) => {
                // One cell by stat
                const valueClassname = classNames(style.value, style.cell, {
                  [style.max]: stat === maxValues[index],
                  [style.selected]: index === selectedProperty,
                  [style.notOnCompactVersion]: properties && !properties[index]?.compactVersion
                });
                return (
                  <td key={index} className={valueClassname}>
                    {stat}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </BlockTemplate>
  );
};

PlayersTable.displayName = 'PlayersTable';

export default PlayersTable;
